import { DeleteIcon } from '@chakra-ui/icons'
import { Button, Flex, Heading, HStack, Text, Tooltip } from '@chakra-ui/react'
import type { FormikErrors, FormikState } from 'formik'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { PermissionsEnum } from '../../../utils'
import { MainContentHeadingBox } from '../../layouts/Base/MainContent'
import PermissionGuard from '../PermissionGuard'
import { PathPolicyFormValues } from './PathPolicyForm'

interface Props {
  pathPolicyId: string
  values: PathPolicyFormValues
  dirty: boolean
  errors: FormikErrors<PathPolicyFormValues>
  isSubmitting: boolean
  setFormError: React.Dispatch<React.SetStateAction<string>>
  onOpenDeletePathPolicy(): void
  resetForm(
    nextState?: Partial<FormikState<PathPolicyFormValues>> | undefined,
  ): void
}

const PathPolicyHeader = ({
  pathPolicyId,
  values,
  dirty,
  errors,
  isSubmitting,
  onOpenDeletePathPolicy,
  setFormError,
  resetForm,
}: Props): ReactElement => {
  const { t } = useTranslation()

  return (
    <MainContentHeadingBox>
      {pathPolicyId && pathPolicyId !== 'new' ? (
        <Flex alignItems="center">
          <Flex
            flexDirection="column"
            justify="center"
            marginLeft={4}
            maxWidth={80}
          >
            <Tooltip label={values.name}>
              <Text
                fontSize="lg"
                fontWeight="semibold"
                lineHeight="taller"
                isTruncated
              >
                {values.name}
              </Text>
            </Tooltip>
          </Flex>
        </Flex>
      ) : (
        <Heading as="h2" size="md" fontWeight="semibold" color="appDarkIndigo">
          {t('new_path_policy', 'New path policy')}
        </Heading>
      )}
      <PermissionGuard permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}>
        {(hasPermission: boolean) =>
          hasPermission ? (
            <HStack spacing={4}>
              {pathPolicyId && pathPolicyId !== 'new' && (
                <Button
                  leftIcon={<DeleteIcon />}
                  variant="deleteCTA"
                  onClick={onOpenDeletePathPolicy}
                >
                  {t('delete', 'Delete')}
                </Button>
              )}
              {dirty && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFormError('')
                    resetForm()
                  }}
                >
                  {t('cancel', 'Cancel')}
                </Button>
              )}
              <Button
                type="submit"
                isDisabled={!dirty || Object.keys(errors).length > 0}
                isLoading={isSubmitting}
                loadingText="Saving"
              >
                {t('save', 'Save')}
              </Button>
            </HStack>
          ) : (
            <></>
          )
        }
      </PermissionGuard>
    </MainContentHeadingBox>
  )
}

export default PathPolicyHeader
